import { z } from 'zod';
import { ageYearsSchema, emailSchema, optionalString, phoneSchema, requiredString } from './common';
import { genderSchema } from './enums';
import { signatureDataUrlSchema } from './signature';

/** Matches the previous @IsStrongPassword({minLength:10, minUppercase:1, minSymbols:1, minNumbers:1}). */
const passwordSchema = z
  .string()
  .min(10, 'Password must be at least 10 characters')
  .regex(/[A-Z]/, 'Password must contain an uppercase letter')
  .regex(/[0-9]/, 'Password must contain a number')
  .regex(/[^A-Za-z0-9]/, 'Password must contain a symbol');

const userFields = {
  first_name: requiredString('First name'),
  last_name: optionalString(),
  age: ageYearsSchema,
  gender: genderSchema,
  phone_number: phoneSchema,
  email: emailSchema,
  father_name: requiredString("Father's name"),
  field: requiredString('Field'),
  /** A `Role` id. Assigning the super role is refused unless the actor holds it. */
  role_id: z.coerce.number().int().positive('Role is required'),
  photo_url: z.url().nullish(),
  /**
   * How this person is styled under their signature — "Senior Lab Technologist".
   *
   * Distinct from `field`, which is the bench they work on. `field` answers
   * "who covers Serology this week"; these two answer "what goes on the paper".
   */
  designation: optionalString(),
  qualifications: optionalString(),
  signature_data_url: signatureDataUrlSchema,
};

export const createUserSchema = z.object({
  ...userFields,
  password: passwordSchema,
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

/**
 * Password is optional on update and is re-hashed when present, so a
 * `password` field in a PATCH body never reaches the hash column in plaintext.
 */
export const updateUserSchema = z.object({
  ...userFields,
  password: passwordSchema.optional(),
});
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

// The response shape lives in ./responses.ts alongside every other wire type.
// `password` — and the redundant `salt` column — are never returned.
